/** Ledger item 3: the draft's changes grouped by entity (lead booking · guest "affects N
 *  bookings" · each party member). A row reads label (the record kind's `rowLabel`, else
 *  the field's) · old struck → new bold · markers
 *  (will be reverted, warning, stale, invalid) · revert; clicking it focuses the field.
 *  A queued action reads the same way ("On-site status · queued action": Not checked in →
 *  Checked in"). A conflict's suggested fix sits under its row. A group longer than
 *  `GROUP_ROW_CAP` rows collapses behind "Show all". The group holding the guest's changes
 *  carries the guest event switch ("Guest events: None · Emit user.updated"). Reverting a
 *  row also reverts what the record kind cascades from it (`cascadeTargets`). A record kind
 *  may word a change itself where its two values would read alike (`describeChange`).
 *
 *  "Will be reverted" is the amber sync icon after the label — its sentence and the
 *  server's evidence in the tooltip, in words for assistive tech; the banner above
 *  carries the count. */
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import SyncIcon from '@mui/icons-material/Sync';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  alpha,
  Box,
  Button,
  ButtonBase,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useId, useState, type ReactNode } from 'react';

import { isActionPath, type DraftEntry, type RecordKey } from '../draft/changeSet';
import { useDraft } from '../draft/DraftProvider';
import { diffIdSets } from '../editor/format';
import { useRecordEditor } from '../editor/RecordEditorProvider';
import { ConflictFixes } from '../fields/ConflictFixes';
import type { RecordChangeOutcome, RecordLedgerLine } from '../editor/types';

import { changeExcerpt, GROUP_ROW_CAP, type LedgerGroup, type LedgerRow } from './model';

export function LedgerChanges({
  groups,
  outcomes,
  switchGroup = null,
}: {
  groups: LedgerGroup[];
  /** The preview's normalised rows by change key (notes, server verdicts). */
  outcomes: ReadonlyMap<string, RecordChangeOutcome>;
  /** The group whose header carries the entity's event switch (guest_events). */
  switchGroup?: string | null;
}) {
  return (
    <Box sx={{ display: 'grid', gap: 1.25 }}>
      {groups.map((g) => (
        <ChangeGroup key={g.id} group={g} outcomes={outcomes} withSwitch={g.id === switchGroup} />
      ))}
    </Box>
  );
}

/** The entity's event switch ("Guest events: None · Emit user.updated"): whether the
 *  changes of that entity emit an event. Its own policy — the Emit · Suppress switch under
 *  "On save" governs the booking's events only. */
function EntityEventSwitchRow() {
  const { entitySwitch, setEntitySwitch } = useRecordEditor();
  const id = useId();
  if (!entitySwitch) return null;
  const { option, value, valueLabel } = entitySwitch;
  return (
    <Box
      data-entity-event-switch={option.key}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}
    >
      <Typography id={id} variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {option.label}
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={value}
        onChange={(_, v: string | null) => v && setEntitySwitch(v)}
        aria-labelledby={id}
        sx={{ '& .MuiToggleButton-root': { py: 0.125, px: 1, textTransform: 'none' } }}
      >
        {option.values.map((v) => (
          <ToggleButton key={String(v.value)} value={String(v.value)}>
            {valueLabel(String(v.value))}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

function ChangeGroup({
  group,
  outcomes,
  withSwitch,
}: {
  group: LedgerGroup;
  outcomes: ReadonlyMap<string, RecordChangeOutcome>;
  withSwitch: boolean;
}) {
  const [all, setAll] = useState(false);
  const rows = all ? group.rows : group.rows.slice(0, GROUP_ROW_CAP);
  const hidden = group.rows.length - rows.length;
  return (
    <Box component="section" aria-label={group.title} data-ledger-group={group.id}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
        <Typography variant="body2" component="h4" sx={{ fontWeight: 600, m: 0 }}>
          {group.title}
        </Typography>
        {group.note && (
          <Typography variant="caption" color="text.secondary">
            {group.note}
          </Typography>
        )}
      </Box>
      {withSwitch && <EntityEventSwitchRow />}
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25 }}>
        {rows.map((r) => (
          <ChangeRow key={r.key} row={r} outcome={outcomes.get(r.key)} />
        ))}
      </Box>
      {(hidden > 0 || all) && group.rows.length > GROUP_ROW_CAP && (
        <Button size="small" onClick={() => setAll((v) => !v)} aria-expanded={all} sx={{ mt: 0.25 }}>
          {all ? 'Show fewer' : `Show all ${group.rows.length} changes`}
        </Button>
      )}
    </Box>
  );
}

function Marker({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Typography
      variant="caption"
      component="span"
      sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', color: 'text.primary' }}
    >
      <Box component="span" aria-hidden sx={{ display: 'inline-flex', mt: '1px' }}>
        {icon}
      </Box>
      <span>{children}</span>
    </Typography>
  );
}

/** The amber "will be reverted" mark (plan §1.4): the sync icon, the sentence and the
 *  evidence on hover, the sentence in words for screen readers. */
function RevertMark({ text, evidence }: { text: string; evidence: string }) {
  return (
    <Tooltip
      title={
        <Box sx={{ display: 'grid', gap: 0.25 }}>
          <span>{text}</span>
          {evidence && <span>{evidence}</span>}
        </Box>
      }
    >
      <Box component="span" data-will-revert sx={{ display: 'inline-flex', flex: 'none' }}>
        <SyncIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main' }} />
        <Box component="span" sx={visuallyHidden}>
          {text}
        </Box>
      </Box>
    </Tooltip>
  );
}

function ChangeRow({ row, outcome }: { row: LedgerRow; outcome: RecordChangeOutcome | undefined }) {
  const { config, widgetOf, formatValue, validateValue, focusField, errorsFor } = useRecordEditor();
  const { state, dispatch } = useDraft();
  const { rec, path, entry, field } = row;
  const label = row.label ?? field?.label ?? path;
  const queued = isActionPath(path);
  const fmt = (v: unknown) => (field ? formatValue(field, rec, v) : String(v ?? '—'));

  // A widget that words its changes as lines (the contacts: "Contact added · …") gets them
  // here, each with its own revert — buttons of their own, so they sit under the row's
  // button.
  const widget = field ? widgetOf(field) : undefined;
  const lines = widget?.ledgerLines ? widget.ledgerLines(entry.from, entry.to) : null;
  // The preview's notes, less what the lines already say.
  const notes = lines && widget?.ledgerNotes ? widget.ledgerNotes(outcome?.notes ?? []) : (outcome?.notes ?? []);
  // The record kind's words for a change whose two values would read alike.
  const described = !lines && field ? config.describeChange?.(field, rec, entry.from, entry.to) : undefined;
  let value: ReactNode;
  if (lines) {
    value = (
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {lines.length === 0 ? 'No visible change' : `${lines.length} ${lines.length === 1 ? 'change' : 'changes'}`}
      </Box>
    );
  } else if (field?.widget === 'tags') {
    const { added, removed } = diffIdSets(entry.from, entry.to);
    const names = (ids: Array<number | string>) => ids.map((id) => fmt([id])).join(', ');
    value = (
      <>
        {added.length > 0 && <b>+ {names(added)}</b>}
        {added.length > 0 && removed.length > 0 && ' '}
        {removed.length > 0 && <Box component="s" sx={{ color: 'text.secondary' }}>− {names(removed)}</Box>}
      </>
    );
  } else if (described) {
    value = <b>{described}</b>;
  } else {
    // Long text (notes) is for the field to show; the row shows where it changed.
    const max = field?.widget === 'textarea' || field?.widget === 'text_lazy' ? 40 : 60;
    const [before, after] = changeExcerpt(fmt(entry.from), fmt(entry.to), max);
    value = (
      <>
        <Box component="s" sx={{ color: 'text.secondary' }}>
          {before}
        </Box>
        <ArrowRightAltIcon aria-hidden sx={{ fontSize: 16, mx: 0.25, verticalAlign: 'middle', color: 'text.secondary' }} />
        <Box component="span" sx={visuallyHidden}>
          {' to '}
        </Box>
        <b>{after}</b>
      </>
    );
  }

  const verdict = outcome?.verdict ?? (field ? config.verdictOf(rec, field, state) : null);
  const willRevert = verdict?.verdict === 'overwrites';
  const validation = field ? validateValue(field, entry.to, entry.from) : null;
  const errors = [...(validation ? [validation] : []), ...errorsFor(rec, path)];
  // The field's static warning, then the change's own — once each (a record kind may
  // also raise the static one as a change warning, for the field's caption).
  const warnings = [
    ...new Set([...(field?.warning ? [field.warning] : []), ...(config.changeWarnings?.(rec, path, entry.to) ?? [])]),
  ];
  const managedBy = config.managedBy ?? 'The feed';

  return (
    <Box
      component="li"
      data-change-row={row.key}
      data-stale={entry.stale ? true : undefined}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'start',
        borderRadius: 1,
        bgcolor: (t) => (entry.stale ? alpha(t.palette.warning.main, 0.12) : 'transparent'),
        '&:hover': { bgcolor: (t) => (entry.stale ? alpha(t.palette.warning.main, 0.16) : t.palette.action.hover) },
      }}
    >
      <ButtonBase
        onClick={() => focusField(rec, path)}
        title={`Go to ${label}`}
        sx={{
          display: 'grid',
          gap: 0.25,
          // ButtonBase centres its content; a row reads from the left.
          justifyContent: 'stretch',
          justifyItems: 'start',
          width: '100%',
          textAlign: 'left',
          px: 0.75,
          py: 0.5,
          borderRadius: 1,
          minWidth: 0,
          '&.Mui-focusVisible': { boxShadow: (t) => `inset 0 0 0 2px ${t.palette.primary.main}` },
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span>
            {label}
            {queued ? ' · queued action' : ''}
          </span>
          {willRevert && (
            <RevertMark
              text={`${managedBy}'s next ${config.feedEvent ?? 'update'} overwrites this`}
              evidence={verdict?.evidence ?? ''}
            />
          )}
        </Typography>
        <Typography variant="body2" component="span" sx={{ overflowWrap: 'anywhere' }}>
          {value}
        </Typography>
        {entry.stale?.gone ? (
          <Marker icon={<SyncProblemIcon sx={{ fontSize: 14, color: 'warning.main' }} />}>
            Deleted elsewhere — this edit can’t be saved. Drop it (↺).
          </Marker>
        ) : entry.stale ? (
          <Marker icon={<SyncProblemIcon sx={{ fontSize: 14, color: 'warning.main' }} />}>
            Changed on the server to {fmt(entry.stale.current)}
            {entry.stale.changedBy?.label ? ` by ${entry.stale.changedBy.label}` : ''} — Keep mine or Take theirs
          </Marker>
        ) : null}
        {warnings.map((w) => (
          <Marker key={w} icon={<WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />}>
            {w}
          </Marker>
        ))}
        {errors.map((e) => (
          <Marker key={e} icon={<ErrorOutlineIcon sx={{ fontSize: 14, color: 'error.main' }} />}>
            {e}
          </Marker>
        ))}
        {notes.map((n, i) => (
          // Two notes may read alike (two items of one name): the position keeps them apart.
          <Typography key={`${i}:${n}`} variant="caption" color="text.secondary">
            {n}
          </Typography>
        ))}
      </ButtonBase>
      <Tooltip title={entry.stale?.gone ? `Drop ${label} — deleted elsewhere` : `Revert ${label}`}>
        <IconButton
          size="small"
          aria-label={entry.stale?.gone ? `Drop ${label}` : `Revert ${label}`}
          onClick={() =>
            dispatch({ type: 'REVERT', targets: [{ rec, path }, ...(config.cascadeTargets?.(rec, path, state) ?? [])] })
          }
          sx={{ mt: 0.25 }}
        >
          <ReplayIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      {/* Outside the row's button: a fix is a button of its own. */}
      <Box sx={{ gridColumn: '1 / -1', px: 0.75, '&:empty': { display: 'none' }, pb: 0.5 }}>
        {lines && lines.length > 0 && field?.widget && (
          <ChangeLines widget={field.widget} rec={rec} path={path} entry={entry} lines={lines} />
        )}
        <ConflictFixes rec={rec} path={path} />
      </Box>
    </Box>
  );
}

/** A change's lines (the widget's `ledgerLines`), each with its revert: one undoable step
 *  that takes back that line only ("Revert Contact added · Email a@b.it"). The list and
 *  its items are marked with the widget's name (`data-contacts-lines`,
 *  `data-contacts-line`). */
function ChangeLines({
  widget,
  rec,
  path,
  entry,
  lines,
}: {
  widget: string;
  rec: RecordKey;
  path: string;
  entry: DraftEntry;
  lines: RecordLedgerLine[];
}) {
  const { dispatch } = useDraft();
  return (
    <Box
      component="ul"
      {...{ [`data-${widget}-lines`]: true }}
      sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25 }}
    >
      {lines.map((l) => (
        <Box
          component="li"
          key={l.id}
          {...{ [`data-${widget}-line`]: l.id }}
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, typography: 'body2', minWidth: 0 }}
        >
          <Box component="span" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', pt: '2px' }}>
            {l.text}
          </Box>
          <Tooltip title={`Revert: ${l.text}`}>
            <IconButton
              size="small"
              aria-label={`Revert ${l.text}`}
              sx={{ p: 0.25 }}
              onClick={() =>
                dispatch({
                  type: 'SET',
                  label: `Revert ${l.text}`,
                  changes: [{ rec, path, from: entry.from, to: l.revert(entry.to) }],
                })
              }
            >
              <ReplayIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </Box>
  );
}

// px strings: in sx a bare `1` is 100%.
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
